import { on } from "events";
import { Search, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

export interface Column<T> {
  key: keyof T;
  label: string;
  render?: (row: T) => React.ReactNode;
}

interface TableProps<T> {
  columns: any[];
  data: T[];
  totalCount?: number;
  pageSize?: number;
  loading?: boolean;
  onSearch?: any;
}

export function DynamicTable<T extends Record<string, any>>({
  columns,
  data,
  totalCount,
  pageSize = 10,
  loading,
  onSearch,
}: TableProps<T>) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Client-side pagination (if API doesn't handle pagination)
  const totalPages = Math.ceil((totalCount || data.length) / pageSize);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize]);

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex justify-between items-center">
        <div className="flex items-center w-64 relative">
          {/* Input */}
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 border rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          {/* Clear Icon */}
          {search && (
            <button
              onClick={() => {
                setSearch("");
                onSearch("");
              }}
              className="absolute right-[1rem] top-1/2 -translate-y-1/2 text-red-400 hover:text-red-700"
              type="button"
            >
              <X size={16} />
            </button>
          )}

          {/* Search Button */}
          <button
            onClick={() => onSearch(search)}
            disabled={!search.trim()}
            className={`ml-2 px-3 py-3 rounded-md bg-indigo-600 text-white font-semibold
      disabled:bg-gray-300 disabled:cursor-not-allowed
      hover:enabled:bg-indigo-700 transition-colors duration-200`}
          >
            <Search size={16} />
          </button>
        </div>
        <span className="text-sm text-gray-600">Total: {totalCount}</span>
      </div>

      {/* Table */}
      {paginatedData.length > 0 ? (<div className="overflow-x-auto border rounded-lg">
        
          <table className="min-w-full border-collapse">
            <thead className="bg-[#7A2F4E]">
              <tr>
                {columns.map((col) => (
                  <th
                    key={String(col.key)}
                    className="text-left px-4 py-3 text-sm font-semibold text-white"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`border-t transition-colors duration-200
          ${
            rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"
          }  // alternate row colors
          hover:bg-indigo-50`}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3 text-sm">
                        {col.render
                          ? col.render(row[col.key as keyof typeof row], row)
                          : row[col.key as keyof typeof row]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center py-6 text-gray-500"
                  >
                    No data found
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center py-6 text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        
      </div>) : (
          <div className="py-10 text-center text-gray-500">No data found.</div>
        )}

      {totalPages > 1 && (
        <div className="flex justify-end items-center gap-2 flex-wrap">
          <button
            disabled={page === 1}
            onClick={() => setPage(1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            First
          </button>

          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          {Array.from({ length: totalPages }).map((_, index) => {
            const pageNumber = index + 1;
            return (
              <button
                key={pageNumber}
                onClick={() => setPage(pageNumber)}
                className={`px-3 py-1 border rounded text-sm ${
                  page === pageNumber
                    ? "bg-black text-white"
                    : "bg-white hover:bg-gray-100"
                }`}
              >
                {pageNumber}
              </button>
            );
          })}

          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>

          <button
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}
